import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Palette } from "lucide-react";

const FIELDS = [
  { key: "company_name", label: "Company Name", type: "text" },
  { key: "tagline", label: "Tagline / Subtitle", type: "text" },
  { key: "logo_url", label: "Logo URL", type: "url" },
  { key: "favicon_url", label: "Favicon URL", type: "url" },
  { key: "login_bg_url", label: "Login Background URL", type: "url" },
  { key: "primary_color", label: "Primary Color", type: "color" },
  { key: "secondary_color", label: "Secondary Color", type: "color" },
  { key: "footer_text", label: "Footer Text", type: "text" },
  { key: "support_email", label: "Support Email", type: "email" },
];

export default function Branding() {
  const { branding, refresh } = useBranding();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (branding) {
      const seed = {};
      for (const f of FIELDS) seed[f.key] = branding[f.key] || "";
      setForm(seed);
    }
  }, [branding]);

  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/branding", form);
      await refresh();
      toast.success("Branding updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update branding");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8 max-w-5xl" data-testid="branding-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-9 h-9 rounded-md border border-zinc-200 grid place-items-center bg-white">
          <Palette className="w-4 h-4 text-zinc-700" />
        </div>
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight">White-Label Branding</h1>
          <p className="text-sm text-zinc-500 mt-0.5">Configure your organization's identity — used on login, sidebar, PDFs and email templates.</p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2 border border-zinc-200 rounded-lg bg-white p-6 space-y-4">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <Label htmlFor={f.key} className="text-xs">{f.label}</Label>
              {f.type === "color" ? (
                <div className="flex items-center gap-3 mt-1">
                  <input
                    id={f.key}
                    type="color"
                    value={form[f.key] || "#000000"}
                    onChange={(e) => setField(f.key, e.target.value)}
                    className="h-9 w-14 rounded border border-zinc-200 cursor-pointer bg-transparent"
                    data-testid={`branding-${f.key}`}
                  />
                  <Input
                    value={form[f.key] || ""}
                    onChange={(e) => setField(f.key, e.target.value)}
                    className="h-9 font-mono text-xs w-32"
                    placeholder="#000000"
                  />
                </div>
              ) : (
                <Input
                  id={f.key}
                  type={f.type}
                  value={form[f.key] || ""}
                  onChange={(e) => setField(f.key, e.target.value)}
                  className="mt-1 h-9"
                  data-testid={`branding-${f.key}`}
                />
              )}
            </div>
          ))}
          <div className="pt-2">
            <Button onClick={save} disabled={saving} data-testid="branding-save">
              {saving ? "Saving…" : "Save Branding"}
            </Button>
          </div>
        </div>

        <div className="border border-zinc-200 rounded-lg bg-white p-6 space-y-4 h-fit">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Live Preview</div>
          <div
            className="rounded-md border border-zinc-200 p-4"
            style={{ background: `linear-gradient(135deg, ${form.primary_color || "#18181b"}, ${form.secondary_color || "#3f3f46"})` }}
          >
            <div className="flex items-center gap-2 text-white">
              {form.logo_url ? (
                <img src={form.logo_url} alt="logo" className="w-8 h-8 rounded bg-white/10 p-1 object-contain" />
              ) : (
                <div className="w-8 h-8 rounded bg-white/10 grid place-items-center text-xs font-bold">
                  {(form.company_name || "D").slice(0, 1)}
                </div>
              )}
              <div className="leading-tight">
                <div className="text-sm font-bold">{form.company_name || "Company"}</div>
                <div className="text-[10px] opacity-70 line-clamp-1">{form.tagline}</div>
              </div>
            </div>
          </div>
          <div className="text-xs text-zinc-500 leading-relaxed">
            Primary/secondary colors drive the login gradient, badges, and future PDF/email templates.
            Logo &amp; favicon apply on the next page reload.
          </div>
        </div>
      </div>
    </div>
  );
}
